import dynamic from 'next/dynamic';
import { useRef } from 'react';
import PageFooter from '@/common/PageFooter';
import PageHeader from '@/common/PageHeader';
import dynamicLoader from '@/vdp/dynamicLoader';
import { MobileGallery } from '@/vdp/Gallery';
import Hero from '@/vdp/Hero';
import LowerSections from '@/vdp/VehicleDetailsContainer/LowerSections';
import ShowConditionally from '@/vdp/VehicleDetailsContainer/ShowConditionally';
import VehicleInfoCard from '@/vdp/VehicleInfoCard';
import { isTest } from '~/constants/environment';
import Section, { SectionId } from '~/constants/vdp/section';
import NativeError from '~/native/NativeError';
import { GridLayout, MainContentWrapper } from './styles';
import {
  DealershipContentWrapper,
  Wrapper
} from './VehicleDetailsContainer.styles';

const AboutThePrice = dynamic(
  () => import(/* webpackChunkName: "AboutThePrice" */ '@/vdp/AboutThePrice'),
  { ssr: false }
);

const DealershipMobileCta = dynamic(
  () =>
    import(
      /* webpackChunkName: "DealershipMobileCta" */ '@/vdp/MobileCta/DealershipMobileCta'
    ),
  { ssr: false }
);

const DealershipPickupAndDelivery = dynamic(
  () =>
    import(
      /* webpackChunkName: "DealershipPickupAndDelivery" */ '@/vdp/PickupAndDelivery/DealershipPickupAndDelivery'
    ),
  { ssr: false }
);

const Disclaimer = dynamic(
  () => import(/* webpackChunkName: "Disclaimer" */ '@/vdp/Disclaimer'),
  { ssr: false, loading: dynamicLoader(Section.Disclaimer) }
);

const FeatureBranch = dynamic(
  () =>
    import(/* webpackChunkName: "FeatureBranch" */ '@/common/FeatureBranch'),
  { ssr: false }
);

const FeaturesAndHighlights = dynamic(
  () =>
    import(
      /* webpackChunkName: "FeaturesAndHighlights" */ '@/vdp/FeaturesAndHighlights'
    ),
  { ssr: false }
);

const Modals = dynamic(
  () => import(/* webpackChunkName: "Modals" */ './Modals'),
  { ssr: false }
);

const DealershipPriceDetails = dynamic(
  () =>
    import(
      /* webpackChunkName: "DealershipPriceDetails" */ '@/vdp/PriceDetails/DealershipPriceDetails'
    ),
  { ssr: false }
);

const StickyBanner = dynamic(
  () => import(/* webpackChunkName: "StickyBanner" */ '@/vdp/StickyBanner'),
  { ssr: false }
);

const VehicleDetails = dynamic(
  () => import(/* webpackChunkName: "VehicleDetails" */ '@/vdp/VehicleDetails'),
  { ssr: false }
);

export default function VehicleDetailsDealershipContainer() {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <PageHeader />
      {isTest && <FeatureBranch />}
      <NativeError />
      <Wrapper
        ref={wrapperRef}
        id={SectionId.Root}
        data-testid="vdp-dealership"
      >
        <DealershipContentWrapper>
          <GridLayout ref={gridRef}>
            <Hero />
            <VehicleInfoCard />
            <MainContentWrapper>
              <ShowConditionally wip={false} operation="and" mobile>
                <MobileGallery />
              </ShowConditionally>
              <ShowConditionally aboutPrice>
                <AboutThePrice gridRef={gridRef} wrapperRef={wrapperRef} />
              </ShowConditionally>
              <DealershipPickupAndDelivery />
              <FeaturesAndHighlights
                gridRef={gridRef}
                wrapperRef={wrapperRef}
              />
              <VehicleDetails />
              <DealershipPriceDetails
                gridRef={gridRef}
                wrapperRef={wrapperRef}
              />
              <LowerSections gridRef={gridRef} wrapperRef={wrapperRef} />
            </MainContentWrapper>
          </GridLayout>
        </DealershipContentWrapper>
        <Disclaimer />
        <ShowConditionally mobile>
          <StickyBanner />
        </ShowConditionally>
        <ShowConditionally mobile>
          <DealershipMobileCta />
        </ShowConditionally>
      </Wrapper>
      <PageFooter />
      <Modals />
    </>
  );
}
